import { Dropdown } from 'antd';
import { EllipsisOutlined } from '@ant-design/icons';
import { ImportRecord, ImportStatus } from '../../../../types/Import';
import { useState } from 'react';
import dayjs from 'dayjs';
import { getUserProfilePictureUrl } from '../../../../common/userHelper';

interface ImportPreviousImportRowProps {
  importRecord: ImportRecord;
  onProceedToMapping: (importId: number) => void;
  onDelete: (importRecord: ImportRecord) => void;
}

const statusBadgeConfig: Record<ImportStatus, { label: string; className: string } | null> = {
  [ImportStatus.PENDING]: { label: 'Pending', className: 'bg-neutral-200 text-neutral-900' },
  [ImportStatus.PARSED]: { label: 'Ready', className: 'bg-blue-100 text-blue-700' },
  [ImportStatus.MAPPING]: { label: 'Mapping', className: 'bg-blue-100 text-blue-700' },
  [ImportStatus.IN_PROGRESS]: { label: 'In Progress', className: 'bg-orange-100 text-orange-700' },
  [ImportStatus.DONE]: { label: 'Done', className: 'bg-green-100 text-green-700' },
  [ImportStatus.FAILED]: { label: 'Failed', className: 'bg-red-100 text-red-700' },
};

export const ImportPreviousImportRow = ({
  importRecord,
  onProceedToMapping,
  onDelete,
}: ImportPreviousImportRowProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const badge = statusBadgeConfig[importRecord.status];

  const canProceedToMapping =
    importRecord.status === ImportStatus.PARSED || importRecord.status === ImportStatus.MAPPING;
  const canDelete = importRecord.status !== ImportStatus.IN_PROGRESS;

  const menuItems = [
    ...(canProceedToMapping
      ? [
          {
            key: 'mapping',
            label: 'Proceed to mapping',
            onClick: () => onProceedToMapping(importRecord.id),
          },
        ]
      : []),
    ...(canDelete
      ? [
          {
            key: 'delete',
            label: <span className="text-red-600">Delete Import</span>,
            onClick: () => onDelete(importRecord),
          },
        ]
      : []),
  ];

  const truncatedFileName =
    importRecord.fileName.length > 35
      ? importRecord.fileName.slice(0, 35) + '...'
      : importRecord.fileName;

  return (
    <div className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-neutral-100 group">
      <div className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer" onClick={() => onProceedToMapping(importRecord.id)}>
        <div className="flex flex-col gap-0.5 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className="text-brand-grey-dark text-sm font-medium truncate"
              title={importRecord.fileName}
            >
              {truncatedFileName}
            </span>
            {badge && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badge.className}`}>
                {badge.label}
              </span>
            )}
          </div>
          <div className="text-neutral-700 text-xs flex items-center gap-1">
            <span>{dayjs(importRecord.createdAt).format('MMM D, YYYY')}</span>
            <span>·</span>
            <span>Import by</span>
            {getUserProfilePictureUrl(importRecord.createdByUser.profilePicturePath ?? null) ? (
              <img
                src={getUserProfilePictureUrl(importRecord.createdByUser.profilePicturePath ?? null) ?? undefined}
                alt=""
                className="w-4 h-4 rounded-full"
              />
            ) : (
              <div className="w-4 h-4 rounded-full bg-neutral-300 flex items-center justify-center text-[8px] text-neutral-700">
                {importRecord.createdByUser.fullName.charAt(0).toUpperCase()}
              </div>
            )}
            <span className="font-medium text-neutral-900">
              {importRecord.createdByUser.fullName}
            </span>
          </div>
        </div>
      </div>

      <Dropdown
        menu={{ items: menuItems }}
        trigger={['click']}
        open={menuOpen}
        onOpenChange={setMenuOpen}
      >
        <button className="w-7 h-7 flex items-center justify-center rounded hover:bg-neutral-200 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer bg-transparent border-none" onClick={(e) => e.stopPropagation()}>
          <EllipsisOutlined className="text-neutral-900" />
        </button>
      </Dropdown>
    </div>
  );
};
